import React, { useState } from 'react';
import moment from 'moment';
import { Meal } from '../../types';
import { MEAL_CATEGORIES } from '../../utils/constants';
import MealPreview from './MealPreview';

const HIGHLIGHT_CATEGORIES = new Set(['Occasion spéciale', 'Restaurant']);
const DAYS_HEADER = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const getCategoryEmoji = (category: string) =>
  MEAL_CATEGORIES.find(c => c.name === category)?.emoji ?? '🍽️';

interface MonthViewProps {
  currentMonthStr: string;
  meals: Meal[];
  onDayClick: (dateString: string) => void;
}

const MonthView: React.FC<MonthViewProps> = ({ currentMonthStr, meals, onDayClick }) => {
  const today = moment().format('YYYY-MM-DD');
  const monthMoment = moment(currentMonthStr);
  const gridStart = monthMoment.clone().startOf('month').startOf('isoWeek');
  const cells = Array.from({ length: 42 }, (_, i) => gridStart.clone().add(i, 'days'));
  const [previewMeal, setPreviewMeal] = useState<Meal | null>(null);

  const getMealsForDay = (dateString: string) =>
    meals.filter(m => m.dateString === dateString);

  return (
    <>
      <div className="bg-white rounded-xl shadow-soft p-3 md:p-4">
        {/* Day headers */}
        <div className="grid grid-cols-7 mb-1">
          {DAYS_HEADER.map(d => (
            <div key={d} className="text-center text-xs font-semibold text-gray-400 uppercase py-1">
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 gap-0.5">
          {cells.map(day => {
            const dateString = day.format('YYYY-MM-DD');
            const isToday = dateString === today;
            const isCurrentMonth = day.month() === monthMoment.month();
            const dayMeals = getMealsForDay(dateString);

            return (
              <div
                key={dateString}
                className={[
                  'rounded-lg p-1 md:p-1.5 text-left min-h-[56px] md:min-h-[84px] flex flex-col',
                  isToday ? 'ring-1 ring-primary/50 bg-primary/5' : '',
                  !isCurrentMonth ? 'opacity-25' : '',
                ].join(' ')}
              >
                {/* Day number — clicking navigates to that week */}
                <button
                  type="button"
                  onClick={() => onDayClick(dateString)}
                  className={`text-xs font-bold mb-1 text-left hover:text-primary transition-colors ${isToday ? 'text-primary' : 'text-gray-500'}`}
                >
                  {day.format('D')}
                </button>

                {/* Meal pills — clicking opens preview */}
                <div className="flex flex-col gap-0.5 flex-1">
                  {dayMeals.slice(0, 3).map(meal => {
                    const isHighlight = HIGHLIGHT_CATEGORIES.has(meal.category);
                    return (
                      <button
                        key={meal._id}
                        type="button"
                        onClick={() => setPreviewMeal(meal)}
                        className={`w-full text-left rounded px-1 py-0.5 leading-tight truncate transition-opacity hover:opacity-75 ${
                          isHighlight
                            ? 'bg-pink-100 text-pink-800 font-semibold text-[10px] md:text-[11px]'
                            : 'bg-gray-100 text-gray-600 text-[10px] md:text-[11px]'
                        }`}
                      >
                        <span className="hidden md:inline">{getCategoryEmoji(meal.category)} {meal.name}</span>
                        <span className="md:hidden">{getCategoryEmoji(meal.category)}</span>
                      </button>
                    );
                  })}
                  {dayMeals.length > 3 && (
                    <div className="text-[9px] text-gray-400 px-1">+{dayMeals.length - 3}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-gray-400 text-center mt-3">
          Cliquer sur le numéro du jour pour planifier la semaine · sur un repas pour les détails
        </p>
      </div>

      <MealPreview meal={previewMeal} onClose={() => setPreviewMeal(null)} />
    </>
  );
};

export default MonthView;
